/**
 * Examination Progress Component
 * Shows progress of token examination process with detailed metrics
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import useTokenExaminerStore from '../stores/tokenExaminerStore';
import { getFaanaHealthColor } from '../utils/faanaScoring';
import { useEntityContext } from '../../EntityContextSwitch';
import api from '../../../services/api';

const EXAMINATION_PHASES = [
  {
    key: 'discovery',
    label: 'Entity Discovery',
    description: 'Identifying entities and potential relationships',
    icon: <PsychologyIcon />,
  },
  {
    key: 'analysis',
    label: 'Relationship Analysis',
    description: 'Analyzing relationship strength and dimensions',
    icon: <AssessmentIcon />,
  },
  {
    key: 'confirmation',
    label: 'Human Confirmation',
    description: 'Manual review and confirmation of relationships',
    icon: <CheckCircleIcon />,
  },
  {
    key: 'insights',
    label: 'Insight Generation',
    description: 'Generating actionable insights and recommendations',
    icon: <TimelineIcon />,
  },
];

// eslint-disable-next-line max-lines-per-function
function ExaminationProgress() {
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [examining, setExamining] = useState(false);
  const { currentEntity } = useEntityContext();

  const {
    relationships,
    entities,
    examinationProgress,
    lastExaminationRun,
    // Actions
    setRelationships,
    setExaminationStatus,
    setNetworkInsights,
    refreshProgress,
    _getFilteredRelationships,
  } = useTokenExaminerStore();

  // Calculate progress metrics
  const metrics = React.useMemo(() => {
    const totalRelationships = relationships.length;
    const confirmedRelationships = relationships.filter(r => r.isConfirmed).length;
    const unconfirmedRelationships = totalRelationships - confirmedRelationships;
    const criticalRelationships = relationships.filter(r => (r.overallHealth || 0) < 40).length;
    const highConfidenceUnconfirmed = relationships.filter(r =>
      !r.isConfirmed && r.confidence === 'HIGH'
    ).length;

    const confirmationRate = totalRelationships > 0 ?
      (confirmedRelationships / totalRelationships) * 100 : 0;

    const averageHealth = totalRelationships > 0 ?
      relationships.reduce((sum, r) => sum + (r.overallHealth || 0), 0) / totalRelationships : 0;

    return {
      totalRelationships,
      confirmedRelationships,
      unconfirmedRelationships,
      criticalRelationships,
      highConfidenceUnconfirmed,
      confirmationRate,
      averageHealth,
      totalEntities: entities.size,
    };
  }, [relationships, entities]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshProgress();
    } catch (error) {
      console.error('Error refreshing progress:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleStartExamination = async () => {
    if (!currentEntity || examining) return;

    setExamining(true);
    try {
      // Trigger the actual token examination
      const result = await api.examineTokensManual(currentEntity.id);

      if (result.examined > 0) {
        // Reload data after examination completes
        await loadEntityData();

        // Show success message
        console.log(`Examined ${result.examined} tokens successfully`);
      }

      if (result.failed > 0) {
        console.warn(`${result.failed} tokens failed examination`);
      }
    } catch (err) {
      console.error('Error examining tokens:', err);
    } finally {
      setExamining(false);
    }
  };

  const loadEntityData = async () => {
    if (!currentEntity) return;

    try {
      // Load examination status
      const status = await api.getTokenExaminationStatus(currentEntity.id);
      setExaminationStatus(status);

      // Load existing relationships
      const relResponse = await api.getEntityRelationships(currentEntity.id, {
        includeAssessment: true,
        limit: 1000,
      });
      setRelationships(relResponse.relationships || []);

      // Set network insights if available
      if (relResponse.summary) {
        setNetworkInsights({
          overallHealth: relResponse.summary.averageHealth || 0,
          keyPatterns: [],
          systemicFactors: [],
          culturalAlignment: 'moderate',
          recommendedInterventions: [],
        });
      }
    } catch (err) {
      console.error('Error loading entity data:', err);
    }
  };

  const getPhaseStatus = (phaseKey) => {
    if (!examinationProgress) return 'pending';

    const phase = examinationProgress.phases?.[phaseKey];
    if (!phase) return 'pending';

    if (phase.completed) return 'completed';
    if (phase.inProgress) return 'in_progress';
    if (phase.error) return 'error';
    return 'pending';
  };

  const getPhaseProgress = (phaseKey) => {
    if (!examinationProgress) return 0;
    return examinationProgress.phases?.[phaseKey]?.progress || 0;
  };

  const renderPhaseItem = (phase) => {
    const status = getPhaseStatus(phase.key);
    const progress = getPhaseProgress(phase.key);

    const getStatusIcon = () => {
      switch (status) {
        case 'completed':
          return <CheckCircleIcon sx={{ color: '#4CAF50' }} />;
        case 'in_progress':
          return <CircularProgress size={20} />;
        case 'error':
          return <ErrorIcon sx={{ color: '#F44336' }} />;
        default:
          return <ScheduleIcon sx={{ color: '#9E9E9E' }} />;
      }
    };

    const getStatusColor = () => {
      switch (status) {
        case 'completed': return '#4CAF50';
        case 'in_progress': return '#2196F3';
        case 'error': return '#F44336';
        default: return '#9E9E9E';
      }
    };

    return (
      <ListItem key={phase.key}>
        <ListItemIcon>
          {getStatusIcon()}
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ component: 'div' }}
          secondaryTypographyProps={{ component: 'div' }}
          primary={phase.label}
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary">
                {phase.description}
              </Typography>
              {status === 'in_progress' && (
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    mt: 1,
                    height: 4,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getStatusColor(),
                    },
                  }}
                />
              )}
            </Box>
          }
        />
      </ListItem>
    );
  };

  return (
    <Paper elevation={2} sx={{ mb: 2 }}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon color="primary" />
            <Typography variant="h6">
              Examination Progress
            </Typography>
            {currentEntity && (
              <Chip
                label={currentEntity.name}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh progress">
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              startIcon={examining ? <CircularProgress size={16} /> : <PlayIcon />}
              onClick={handleStartExamination}
              variant="contained"
              color="primary"
              disabled={!currentEntity || examining}
            >
              {examining ? 'Examining...' : 'Start Examination'}
            </Button>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Overall Progress */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Overall Progress
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {Math.round(metrics.confirmationRate)}% Confirmed
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={metrics.confirmationRate}
            sx={{
              height: 8,
              borderRadius: 4,
              '& .MuiLinearProgress-bar': {
                backgroundColor: getFaanaHealthColor(metrics.confirmationRate),
              },
            }}
          />
        </Box>

        {/* Key Metrics */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label={`${metrics.totalRelationships} Total`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${metrics.confirmedRelationships} Confirmed`}
            size="small"
            color="success"
            variant={metrics.confirmedRelationships > 0 ? "filled" : "outlined"}
          />
          <Chip
            label={`${metrics.unconfirmedRelationships} Pending`}
            size="small"
            color="warning"
            variant={metrics.unconfirmedRelationships > 0 ? "filled" : "outlined"}
          />
          {metrics.criticalRelationships > 0 && (
            <Chip
              label={`${metrics.criticalRelationships} Critical`}
              size="small"
              color="error"
            />
          )}
        </Stack>

        {/* Alerts */}
        {metrics.highConfidenceUnconfirmed > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {metrics.highConfidenceUnconfirmed} high-confidence relationships need confirmation
            </Typography>
          </Alert>
        )}

        {metrics.criticalRelationships > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {metrics.criticalRelationships} relationships have critical health scores
            </Typography>
          </Alert>
        )}

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          {/* Phase Progress */}
          <Typography variant="subtitle2" gutterBottom>
            Examination Phases
          </Typography>
          <List dense>
            {EXAMINATION_PHASES.map(renderPhaseItem)}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Detailed Metrics */}
          <Typography variant="subtitle2" gutterBottom>
            Detailed Metrics
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h4" color="primary" gutterBottom>
                  {metrics.totalEntities}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Entities
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Typography
                  variant="h4"
                  sx={{ color: getFaanaHealthColor(metrics.averageHealth) }}
                  gutterBottom
                >
                  {Math.round(metrics.averageHealth)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Health Score
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h4" color="success.main" gutterBottom>
                  {Math.round(metrics.confirmationRate)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Confirmation Rate
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Last Run Info */}
          {lastExaminationRun && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Last examination: {new Date(lastExaminationRun).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Collapse>
      </Box>
    </Paper>
  );
}

export default ExaminationProgress;
